import type { 
  LoginRequest, 
  LoginResponse, 
  RefreshTokenRequest,
  User,
  ApiResponse,
  UserRole
} from '@/types/api';
import { getUserRoleFromString } from '@/types/api';
import { apiClient } from './api';
import { apiEndpoints, storageKeys } from '@/config/app';

export class AuthService {
  /**
   * Register new user account
   */
  async register(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<ApiResponse<LoginResponse>> {
    const response = await apiClient.post<LoginResponse>(
      apiEndpoints.auth.register,
      userData
    );

    if (response.success && response.data) {
      // Store tokens and user data after successful registration
      apiClient.setTokens(response.data.accessToken, response.data.refreshToken);
      this.setUser(response.data.user);
    }

    return response;
  }

  /**
   * Request password reset
   */
  async forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    return await apiClient.post<{ message: string }>(
      apiEndpoints.auth.forgotPassword,
      { email }
    );
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<ApiResponse<{ message: string }>> {
    return await apiClient.post<{ message: string }>(
      apiEndpoints.auth.resetPassword,
      { token, newPassword }
    );
  }

  /**
   * Change current user password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<{ message: string }>> {
    return await apiClient.post<{ message: string }>(
      apiEndpoints.auth.changePassword,
      { currentPassword, newPassword }
    );
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await apiClient.post<LoginResponse>(
      apiEndpoints.auth.login,
      credentials
    );

    if (response.success && response.data) {
      // Store tokens and user data
      apiClient.setTokens(response.data.accessToken, response.data.refreshToken);
      this.setUser(response.data.user);
    }

    return response;
  }

  /**
   * Logout user and clear stored data
   */
  async logout(): Promise<void> {
    try {
      // Notify the server about logout
      await apiClient.post(apiEndpoints.auth.logout);
    } catch (error) {
      // Even if the server call fails, we should clear local data
      console.warn('Logout server call failed:', error);
    } finally {
      // Clear all stored data
      apiClient.logout();
      this.clearUser();
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<ApiResponse<{ accessToken: string }>> {
    const response = await apiClient.post<{ accessToken: string }>(
      apiEndpoints.auth.refresh,
      { refreshToken } as RefreshTokenRequest
    );

    if (response.success && response.data) {
      // Update stored access token
      localStorage.setItem(storageKeys.accessToken, response.data.accessToken);
    }

    return response;
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<ApiResponse<User>> {
    const response = await apiClient.get<User>(apiEndpoints.auth.profile);

    if (response.success && response.data) {
      // Update stored user data
      this.setUser(response.data);
    }

    return response;
  }

  /**
   * Update current user profile
   */
  async updateProfile(updates: Partial<User>): Promise<ApiResponse<User>> {
    const response = await apiClient.put<User>(apiEndpoints.auth.profile, updates);

    if (response.success && response.data) {
      // Update stored user data
      this.setUser(response.data);
    }

    return response;
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    return apiClient.isAuthenticated() && this.getStoredUser() !== null;
  }

  /**
   * Get current user from storage
   */
  getCurrentUser(): User | null {
    return this.getStoredUser();
  }

  /**
   * Store user data in localStorage
   */
  private setUser(user: User): void {
    // Ensure role is converted from string to enum if needed
    if (typeof user.role === 'string') {
      console.log('Converting role from string to enum during setUser:', user.role);
      user.role = getUserRoleFromString(user.role);
    }
    localStorage.setItem(storageKeys.user, JSON.stringify(user));
  }

  /**
   * Get user data from localStorage with role migration
   */
  private getStoredUser(): User | null {
    try {
      const userStr = localStorage.getItem(storageKeys.user);
      if (!userStr) return null;
      
      const user = JSON.parse(userStr);
      
      // Migrate old string-based roles to new integer-based roles
      if (typeof user.role === 'string') {
        console.log('Migrating user role from string to integer:', user.role);
        user.role = getUserRoleFromString(user.role);
        // Save migrated user data
        this.setUser(user);
      }
      
      return user;
    } catch (error) {
      console.error('Error parsing stored user data:', error);
      return null;
    }
  }

  /**
   * Clear user data from localStorage
   */
  private clearUser(): void {
    localStorage.removeItem(storageKeys.user);
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(storageKeys.accessToken);
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(storageKeys.refreshToken);
  }

  /**
   * Check if current user has specific role
   */
  hasRole(role: UserRole): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  /**
   * Check if current user has any of the specified roles
   */
  hasAnyRole(roles: UserRole[]): boolean {
    const user = this.getCurrentUser();
    return user ? roles.includes(user.role) : false;
  }

  /**
   * Check if current user is admin
   */
  isAdmin(): boolean {
    return this.hasRole(UserRole.Admin);
  }

  /**
   * Check if current user is agent
   */
  isAgent(): boolean {
    return this.hasAnyRole([UserRole.Admin, UserRole.Agent]);
  }

  /**
   * Get user's full name
   */
  getUserDisplayName(): string {
    const user = this.getCurrentUser();
    return user ? user.fullName || `${user.firstName} ${user.lastName}`.trim() : '';
  }

  /**
   * Check if access token is expired (client-side check only)
   */
  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      // Decode JWT token (basic check - not for security validation)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= exp;
    } catch (error) {
      // If we can't decode the token, consider it expired
      return true;
    }
  }

  /**
   * Get time until token expires (in minutes)
   */
  getTokenExpiryTime(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      return Math.max(0, Math.floor((exp - now) / 60000)); // Return minutes
    } catch (error) {
      return null;
    }
  }
}

// Create and export singleton instance
export const authService = new AuthService();